import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Star, ChevronDown, Sparkles } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { imgUrl } from "../api";
import { EASE } from "./Reveal";

const WhatsAppIcon = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
);

export { WhatsAppIcon };

export default function Hero() {
    const { content, t, lang, cf } = useSite();
    const ref = useRef(null);

    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
    const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(my, { stiffness: 70, damping: 16 });
    const rotateY = useSpring(mx, { stiffness: 70, damping: 16 });

    const onTilt = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set(((e.clientX - r.left) / r.width - 0.5) * 16);
        my.set(-((e.clientY - r.top) / r.height - 0.5) * 16);
    };
    const resetTilt = () => {
        mx.set(0);
        my.set(0);
    };

    const lines = (cf("hero_title") || "").split("\n").filter(Boolean);
    const waLink = `https://wa.me/${content?.whatsapp || ""}?text=${encodeURIComponent(
        t.wa.hero.replace("{name}", content?.business_name || "J&S Beauty Studio")
    )}`;

    return (
        <section
            id="inicio"
            ref={ref}
            data-testid="hero-section"
            onMouseMove={onTilt}
            onMouseLeave={resetTilt}
            className="relative min-h-screen flex items-center overflow-hidden grain"
        >
            {/* Parallax background */}
            <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
                {content?.hero_bg && (
                    <img
                        src={content.hero_bg}
                        alt=""
                        className="w-full h-full object-cover scale-110"
                        aria-hidden="true"
                    />
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/70 to-[#0A0A0B]" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(10,10,11,0.75)_100%)]" />
            </motion.div>

            <motion.div
                style={{ opacity: contentOpacity, y: contentY }}
                className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 w-full pt-32 pb-24 grid lg:grid-cols-2 gap-16 items-center"
            >
                {/* Copy */}
                <div className="text-center lg:text-left">
                    {content?.announcement && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
                            className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/5 px-4 py-1.5 mb-8"
                            data-testid="hero-announcement"
                        >
                            <Sparkles size={13} className="text-gold" />
                            <span className="text-xs tracking-[0.2em] uppercase text-gold-light">{cf("announcement")}</span>
                        </motion.div>
                    )}

                    <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-amber-50">
                        {lines.map((line, li) => (
                            <span key={li} className="block overflow-hidden pb-1 -mb-1">
                                {line.split(" ").map((w, wi) => (
                                    <motion.span
                                        key={wi}
                                        className={`inline-block will-change-transform ${
                                            li === 1 ? "font-display italic text-gold-gradient" : ""
                                        }`}
                                        initial={{ y: "115%", opacity: 0 }}
                                        animate={{ y: "0%", opacity: 1 }}
                                        transition={{ duration: 0.95, delay: 0.3 + (li * 4 + wi) * 0.09, ease: EASE }}
                                    >
                                        {w}
                                        {"\u00A0"}
                                    </motion.span>
                                ))}
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
                        className="mt-6 text-base sm:text-lg text-zinc-300 font-light leading-relaxed max-w-xl mx-auto lg:mx-0"
                        data-testid="hero-subtitle"
                    >
                        {cf("hero_subtitle")}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
                        className="mt-10 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4"
                    >
                        <a
                            href={waLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-cta-whatsapp"
                            className="inline-flex items-center gap-2.5 bg-gold-gradient text-black font-semibold px-7 py-3.5 rounded-full hover:shadow-[0_0_35px_rgba(212,175,55,0.45)] hover:-translate-y-0.5 transition-all duration-300"
                        >
                            <WhatsAppIcon size={18} />
                            {t.hero.whatsapp}
                        </a>
                        <a
                            href="#servicios"
                            data-testid="hero-cta-services"
                            className="inline-flex items-center gap-2 border border-gold/40 text-gold-light px-7 py-3.5 rounded-full hover:bg-gold/10 hover:border-gold transition-all duration-300"
                        >
                            {t.hero.services}
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.25 }}
                        className="mt-10 flex items-center justify-center lg:justify-start gap-3"
                        data-testid="hero-rating"
                    >
                        <div className="flex gap-0.5">
                            {[...Array(5)].map((_, i) => (
                                <Star key={i} size={15} className={i < 4 ? "text-gold" : "text-gold/40"} fill="currentColor" strokeWidth={0} />
                            ))}
                        </div>
                        <span className="text-sm text-zinc-300">
                            <strong className="text-gold-light">{content?.google_rating || "4.6"}</strong> · {content?.google_count || "7"} {t.hero.reviews}
                        </span>
                    </motion.div>
                </div>

                {/* Logo 3D */}
                <div className="flex justify-center lg:justify-end" style={{ perspective: "1000px" }}>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.82, y: 40 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ duration: 1.1, delay: 0.25, ease: EASE }}
                        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
                        className="relative"
                        data-testid="hero-logo"
                    >
                        <motion.div
                            animate={{ y: [0, -14, 0] }}
                            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                            className="relative"
                        >
                            <div className="absolute -inset-8 bg-gold/15 blur-3xl rounded-full" aria-hidden="true" />
                            <div
                                className="absolute -inset-5 rounded-full border border-dashed border-gold/30 animate-spin-slow"
                                aria-hidden="true"
                            />
                            <div className="relative w-64 h-64 sm:w-80 sm:h-80 lg:w-[26rem] lg:h-[26rem] rounded-full overflow-hidden gold-ring border-2 border-gold/40 bg-[#0D0D0E]">
                                <img
                                    src={imgUrl(content?.logo_url)}
                                    alt={`${content?.business_name || "J&S Beauty Studio"} logo`}
                                    className="w-full h-full object-contain p-8 sm:p-12"
                                    data-testid="hero-logo-image"
                                />
                            </div>
                            <div
                                className="absolute -bottom-4 -left-6 glass border border-gold/30 rounded-full px-5 py-2.5"
                                style={{ transform: "translateZ(50px)" }}
                                data-testid="hero-unisex-badge"
                            >
                                <span className="text-[11px] tracking-[0.35em] uppercase text-gold-light">Unisex</span>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.div>

            <motion.a
                href="#servicios"
                data-testid="hero-scroll-cue"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8, duration: 1 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gold/70 hover:text-gold transition-colors"
                aria-label="Bajar a servicios"
            >
                <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }}>
                    <ChevronDown size={28} />
                </motion.div>
            </motion.a>
        </section>
    );
}
